import React from 'react';
import { Link } from '@tanstack/react-router';
import { Text } from 'src/modules/shared/components/atoms/text';
import { CountBadge } from 'src/modules/shared/components/atoms/count-badge';
import { useId } from 'src/modules/shared/hooks/useId';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

interface SidebarButtonProps {
  to?: string;
  search?: Record<string, unknown>;
  icon?: JSX.Element;
  label: string;
  hidden?: boolean;
  disabled?: boolean;
  className?: string;
  textClassName?: string;
  isCollapsed?: boolean;
  badgeCount?: number;
  onClick?: () => void;
}

const baseClasses = `
  select-none disabled:pointer-events-none transition-colors
  focus:opacity-[0.85] active:opacity-[0.85]
  disabled:opacity-50 disabled:shadow-none
  focus:shadow-none active:shadow-none shadow-none
  flex items-center w-full rounded-md h-8
  bg-transparent hover:bg-neutral-100
  font-sans font-medium text-left text-primary-500 normal-case text-sm
  px-3 gap-2
`;

/**
 * Shared navigation button used by desktop and mobile sidebars.
 */
export const SidebarButton: React.FC<SidebarButtonProps> = ({
  to,
  search,
  icon,
  label,
  hidden = false,
  disabled = false,
  className = '',
  textClassName = '',
  isCollapsed = false,
  badgeCount,
  onClick,
}) => {
  const buttonId = useId();

  if (hidden) return null;

  const renderContent = (isActive = false) => {
    const activeClasses = isActive ? '!bg-neutral-100' : '';
    const classes = `${baseClasses} ${activeClasses} ${className}`;

    return (
      <div className={classes} data-sidebar-item id={buttonId}>
        <div
          data-sidebar-icon
          aria-hidden="true"
          className="flex shrink-0 items-center justify-center"
        >
          {icon}
        </div>
        <div
          data-sidebar-label
          aria-hidden={isCollapsed}
          className="min-w-0 flex-1 overflow-hidden"
        >
          <div className="flex items-center gap-2">
            <Text
              weight={isActive ? 'medium' : 'regular'}
              className={`whitespace-nowrap text-left min-w-0 flex-1 overflow-hidden text-ellipsis ${textClassName}`}
            >
              {label}
            </Text>
            {!isCollapsed && badgeCount && badgeCount > 0 && (
              <CountBadge count={badgeCount} size="2xs" variant="primary" />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (to) {
    return (
      <Tooltip content={label} enabled={isCollapsed} placement="right">
        <Link
          to={to}
          search={search}
          className="flex w-full min-w-0"
          aria-label={isCollapsed ? label : undefined}
          disabled={disabled}
          onClick={onClick}
        >
          {({ isActive }: { isActive: boolean }) => renderContent(isActive)}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={label} enabled={isCollapsed} placement="right">
      <button
        className="flex w-full"
        aria-label={isCollapsed ? label : undefined}
        disabled={disabled}
        onClick={onClick}
        type="button"
      >
        {renderContent()}
      </button>
    </Tooltip>
  );
};
